import {
	AlertTriangle,
	Check,
	InfoCircle,
	XCircle,
	XClose,
} from "@untitledui/icons";
import type { FC, ReactElement, ReactNode } from "react";
import { IconButton } from "@/components/Atoms/IconButton/IconButton";
import { Button } from "@/components/base/buttons/button";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import { cx } from "@/utils/cx";

export const AlertType = {
	Toast: "toast",
	Alert: "alert",
} as const;

export type AlertType = (typeof AlertType)[keyof typeof AlertType];

export const AlertSemantic = {
	Brand: "brand",
	Gray: "gray",
	Info: "info",
	Success: "success",
	Warning: "warning",
	Error: "error",
} as const;

export type AlertSemantic = (typeof AlertSemantic)[keyof typeof AlertSemantic];

export interface AlertAction {
	label: string;
	onPress?: () => void;
}

export interface AlertProps {
	/**
	 * Surface treatment: solid compact toast vs full-width tinted alert.
	 * Brand alert uses a solid brand surface (matches Figma).
	 * @default "toast"
	 */
	type?: AlertType;
	/**
	 * Semantic color / meaning.
	 * @default "success"
	 */
	semantic?: AlertSemantic;
	/** Primary message (extrabold). */
	title?: ReactNode;
	/** Supporting / secondary message. */
	description?: ReactNode;
	/** Show primary text. Defaults to true when `title` is set. */
	showTitle?: boolean;
	/** Show supporting text. Defaults to true when `description` is set. */
	showDescription?: boolean;
	/** Show leading FeaturedIcon. @default true */
	showLeadingIcon?: boolean;
	/** Override default semantic icon. */
	icon?: FC<{ className?: string }>;
	/** Called when dismiss is activated. Dismiss control is always shown. */
	onDismiss?: () => void;
	/** Accessible name for dismiss. */
	dismissAriaLabel?: string;
	/** Secondary (tertiary) action. */
	secondaryAction?: AlertAction;
	/** Primary (secondary hierarchy) action. */
	primaryAction?: AlertAction;
	className?: string;
	"data-testid"?: string;
}

type FeaturedIconColor = "brand" | "gray" | "success" | "warning" | "error";

const DEFAULT_ICONS: Record<AlertSemantic, FC<{ className?: string }>> = {
	brand: InfoCircle,
	gray: InfoCircle,
	info: InfoCircle,
	success: Check,
	warning: AlertTriangle,
	error: XCircle,
};

const FEATURED_ICON_COLOR: Record<AlertSemantic, FeaturedIconColor> = {
	brand: "brand",
	gray: "gray",
	info: "brand",
	success: "success",
	warning: "warning",
	error: "error",
};

const TOAST_SURFACE: Record<AlertSemantic, string> = {
	brand: "bg-brand-solid",
	gray: "bg-secondary-solid",
	info: "bg-utility-blue-700",
	success: "bg-utility-success-600",
	warning: "bg-warning-solid",
	error: "bg-utility-error-700",
};

const ALERT_SURFACE: Record<AlertSemantic, string> = {
	brand: "border border-primary bg-brand-solid",
	gray: "border border-primary bg-tertiary",
	info: "border border-utility-blue-100 bg-utility-blue-50",
	success: "border border-utility-success-100 bg-success-primary",
	warning: "border border-utility-warning-100 bg-warning-primary",
	error: "border border-utility-error-100 bg-error-primary",
};

const ALERT_TEXT: Record<AlertSemantic, string> = {
	brand: "text-white",
	gray: "text-secondary",
	info: "text-blue-900",
	success: "text-success-800",
	warning: "text-utility-warning-700",
	error: "text-utility-error-700",
};

const INFO_FEATURED_ICON_CLASS =
	"bg-utility-blue-100! text-utility-blue-600! before:border-utility-blue-200/12!";

const TOAST_DISMISS_CLASS = [
	"text-fg-white! hover:bg-white/10 hover:text-fg-white!",
	// Override link-gray icon tokens so x-close stays white on solid toast
	"*:data-icon:text-fg-white! hover:*:data-icon:text-fg-white!",
].join(" ");

/**
 * Inline alert or toast notification (Figma Alert 3278:15456).
 *
 * - `type="toast"` — solid, compact; used by the toast system
 * - `type="alert"` — tinted full-width banner for page context (Brand uses solid)
 * - Dismiss (IconButton x-close) is always present
 */
export function Alert({
	type = AlertType.Toast,
	semantic = AlertSemantic.Success,
	title,
	description,
	showTitle,
	showDescription,
	showLeadingIcon = true,
	icon,
	onDismiss,
	dismissAriaLabel = "Dismiss",
	secondaryAction,
	primaryAction,
	className,
	"data-testid": dataTestId = "alert",
}: AlertProps): ReactElement {
	const isToast = type === AlertType.Toast;
	const isSolidSurface = isToast || semantic === AlertSemantic.Brand;
	const Icon = icon ?? DEFAULT_ICONS[semantic];
	const featuredColor = FEATURED_ICON_COLOR[semantic];
	const shouldShowTitle = showTitle ?? title != null;
	const shouldShowDescription = showDescription ?? description != null;
	const role =
		semantic === AlertSemantic.Error || semantic === AlertSemantic.Warning
			? "alert"
			: "status";

	const hasActions = Boolean(secondaryAction || primaryAction);

	return (
		<div
			role={role}
			data-testid={dataTestId}
			data-type={type}
			data-semantic={semantic}
			className={cx(
				"relative flex w-full items-start gap-3 rounded-xl p-4",
				isToast ? TOAST_SURFACE[semantic] : ALERT_SURFACE[semantic],
				className,
			)}
		>
			{showLeadingIcon ? (
				<div className="flex shrink-0 items-center">
					<FeaturedIcon
						icon={Icon}
						size="sm"
						color={
							semantic === AlertSemantic.Brand && !isToast
								? "gray"
								: featuredColor
						}
						theme={isSolidSurface ? "light" : "dark"}
						className={cx(
							!isToast && "rounded-full!",
							semantic === AlertSemantic.Info &&
								isToast &&
								INFO_FEATURED_ICON_CLASS,
							semantic === AlertSemantic.Info &&
								!isToast &&
								"bg-utility-blue-600! text-fg-white! before:border-white/12!",
							semantic === AlertSemantic.Brand &&
								!isToast &&
								"bg-white! text-fg-brand-primary! before:border-transparent!",
						)}
					/>
				</div>
			) : null}

			<div
				className={cx(
					"flex min-w-0 flex-1 items-center self-stretch",
					isToast ? "gap-3" : "gap-5",
				)}
			>
				<div className="flex min-w-0 flex-1 flex-col items-start justify-center gap-0.5">
					{shouldShowTitle ? (
						<p
							className={cx(
								"max-w-full font-display font-extrabold",
								isSolidSurface
									? // Truncate (not nowrap) so long titles stay inside the toast width.
										"truncate text-md leading-6 text-white"
									: cx("text-sm leading-5", ALERT_TEXT[semantic]),
							)}
						>
							{title}
						</p>
					) : null}
					{shouldShowDescription ? (
						<p
							className={cx(
								"max-w-full text-sm font-medium leading-5 break-words",
								// Toast banners are compact — clamp wrapped detail so Sonner height stays usable.
								isToast && "line-clamp-4",
								isSolidSurface ? "text-white" : ALERT_TEXT[semantic],
							)}
						>
							{description}
						</p>
					) : null}
				</div>

				{hasActions ? (
					<div className="flex shrink-0 items-center gap-2">
						{secondaryAction ? (
							<Button
								size="sm"
								color="tertiary"
								onClick={secondaryAction.onPress}
								className={
									isSolidSurface
										? "bg-transparent text-white ring-white/30 hover:bg-white/10"
										: undefined
								}
							>
								{secondaryAction.label}
							</Button>
						) : null}
						{primaryAction ? (
							<Button
								size="sm"
								color="secondary"
								onClick={primaryAction.onPress}
							>
								{primaryAction.label}
							</Button>
						) : null}
					</div>
				) : null}
			</div>

			<div className="flex shrink-0 items-center justify-end self-stretch">
				{/* Figma: IconButton Size=sm, Hierarchy=Link, Shape=Round, Icons/x-close — always present */}
				<IconButton
					aria-label={dismissAriaLabel}
					hierarchy="link"
					size="sm"
					shape="round"
					icon={XClose}
					onClick={onDismiss}
					className={isSolidSurface ? TOAST_DISMISS_CLASS : undefined}
				/>
			</div>
		</div>
	);
}
